<script setup>
import {ref} from "vue";
import GroupCodeBox from "@/components/common/input/groupCodeBox.vue";
import {useLanguageStore} from "@/pinia";
import {useToast} from "@/composables/useToast";

const toast = useToast();
const languageStore = useLanguageStore();

// Form ref
const formRef = ref(null);
const isFormValid = ref(false);

const props = defineProps(['insertModal'])
const emits = defineEmits(['set_insertModal', 'fn_insert'])

const codeId = ref();
const codeNm = ref();
const grpCode = ref();
const ord = ref();
const memo = ref();

// Validation rules
const rules = {
  required: v => !!v || languageStore.get('requiredField')
};

const set_grpCode = (data) => {
  grpCode.value = data;
}

const clear = () => {
  codeId.value = null;
  codeNm.value = null;
  grpCode.value = null;
  ord.value = null;
  memo.value = null;
  formRef.value?.reset();
}

const close = () => {
  emits('set_insertModal', false);
  clear();
}

const insert = async () => {
  // v-form validation
  const {valid} = await formRef.value.validate();

  // Custom validation for GroupCodeBox
  if (!grpCode.value) {
    toast.warning(languageStore.get('pleaseSelectGroupCode'));
    return;
  }

  if (!valid) {
    toast.warning(languageStore.get('pleaseCheckInputFields'));
    return;
  }

  const data = {
    "codeId": codeId.value,
    "codeNm": codeNm.value,
    "grpCode": grpCode.value,
    "ord": ord.value,
    "memo": memo.value
  }
  emits('fn_insert', data);
  close();
}
</script>

<template>
  <v-dialog
      :model-value="insertModal"
      @update:model-value="close"
      width="500"
  >
    <v-card>
      <v-card-title class="modal-header">
        {{ languageStore.get('codeRegistration') }}
      </v-card-title>

      <v-card-text class="modal-body">
        <v-form ref="formRef" v-model="isFormValid">
          <v-text-field
              v-model="codeId"
              :label="languageStore.get('codeId') + ' *'"
              variant="outlined"
              density="compact"
              :rules="[rules.required]"
              class="mb-4"
          />

          <v-text-field
              v-model="codeNm"
              :label="languageStore.get('codeName') + ' *'"
              variant="outlined"
              density="compact"
              :rules="[rules.required]"
              class="mb-4"
          />

          <group-code-box @set_grpCode="set_grpCode" class="mb-4" />

          <v-text-field
              v-model="ord"
              :label="languageStore.get('order')"
              type="number"
              variant="outlined"
              density="compact"
              hide-details="auto"
              class="mb-4"
          />

          <v-text-field
              v-model="memo"
              :label="languageStore.get('memo')"
              variant="outlined"
              density="compact"
              hide-details="auto"
          />
        </v-form>
      </v-card-text>

      <v-card-actions class="modal-footer">
        <v-spacer />
        <v-btn
            color="primary"
            variant="flat"
            @click="insert"
        >
          {{ languageStore.get('save') }}
        </v-btn>
        <v-btn
            variant="outlined"
            @click="close"
        >
          {{ languageStore.get('close') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<style scoped>
/* 모달 헤더 */
.modal-header {
  background-color: #1A3A5C !important;
  color: #FFFFFF !important;
  padding: 16px 24px;
  font-weight: 600;
  font-size: 18px;
}

/* 모달 바디 - Vuetify 기본 padding 제거 */
:deep(.v-card-text.modal-body) {
  padding: 24px !important;
}

/* 모달 바디 내 입력 필드 간격 */
.modal-body :deep(.v-input) {
  margin-bottom: 16px;
}

.modal-body :deep(.v-input:last-child) {
  margin-bottom: 0;
}

/* 모달 푸터 - Vuetify 기본 padding 제거 */
:deep(.v-card-actions.modal-footer) {
  padding: 16px 24px !important;
  border-top: 1px solid #DEE2E6;
  gap: 8px;
}
</style>
